import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Send, Loader2, CheckCircle2, BadgePercent } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Chapter } from "@/components/landing/Chapter";

const SERVICE_OPTIONS = [
  "Mécanique générale",
  "Entretien & vidange",
  "Pneumatiques & géométrie",
  "Freinage & sécurité",
  "Diagnostic électronique",
  "Changement pare-brise (assurance)",
  "Achat / vente véhicule",
  "Autre demande",
];

const INPUT_CLASS =
  "w-full rounded-md border border-white/10 bg-[#07090E] px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#0066FF] focus:shadow-[0_0_0_3px_rgba(0,102,255,0.2)]";

const initialForm = {
  nom: "",
  telephone: "",
  email: "",
  immatriculation: "",
  service: SERVICE_OPTIONS[0],
  date_souhaitee: "",
  message: "",
};

export const ContactForm = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const { user } = useAuth();
  useEffect(() => {
    if (user && user.role === "client") {
      setForm((f) => ({
        ...f,
        nom: f.nom || user.name || "",
        email: f.email || user.email || "",
        telephone: f.telephone || user.telephone || "",
      }));
    }
  }, [user]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.immatriculation) delete payload.immatriculation;
      if (!payload.date_souhaitee) delete payload.date_souhaitee;
      await api.post("/contact", payload);
      setSent(true);
      setForm(initialForm);
      toast.success("Demande envoyée ! Nous vous rappelons sous 24h ouvrées.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-36 bg-[#0A0D16] border-t border-white/5 noise-overlay">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Chapter num="07" title="Contact & devis" accent="Réponse sous 24h ouvrées" />

        <div className="grid lg:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-4"
          >
            <p className="text-base text-slate-300 leading-relaxed">
              Décrivez votre besoin en quelques lignes : nous vous rappelons pour confirmer
              votre rendez-vous ou vous établir un devis gratuit.
            </p>
            <ul className="mt-8 space-y-4">
              {["Devis gratuit et sans engagement", "Confirmation par téléphone", "Véhicule pris en charge toutes marques"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm text-slate-300">
                  <CheckCircle2 className="w-4 h-4 text-[#00E5FF] shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
            <Link
              data-testid="contact-account-perk"
              to="/compte"
              className="mt-8 flex items-start gap-4 rounded-md border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-5 transition-colors duration-300 hover:border-[#F59E0B]"
            >
              <BadgePercent className="w-5 h-5 text-[#F59E0B] shrink-0 mt-0.5" />
              <span className="text-sm text-slate-200 leading-relaxed">
                <strong className="text-[#F59E0B]">Compte client : -40%</strong> sur votre première
                prestation, et suivi de vos demandes en ligne.
              </span>
            </Link>
          </motion.div>

          <motion.form
            data-testid="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-8 rounded-md border border-white/10 bg-[#0E1322]/70 backdrop-blur-xl p-6 md:p-10"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-nom" className="block text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-400 mb-2">Nom *</label>
                <input id="contact-nom" data-testid="contact-input-name" required minLength={2} value={form.nom} onChange={set("nom")} placeholder="Votre nom" className={INPUT_CLASS} />
              </div>
              <div>
                <label htmlFor="contact-tel" className="block text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-400 mb-2">Téléphone *</label>
                <input id="contact-tel" data-testid="contact-input-phone" required minLength={6} type="tel" value={form.telephone} onChange={set("telephone")} placeholder="06 12 34 56 78" className={INPUT_CLASS} />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-400 mb-2">Email</label>
                <input id="contact-email" data-testid="contact-input-email" type="email" value={form.email} onChange={set("email")} placeholder="vous@exemple.fr" className={INPUT_CLASS} />
              </div>
              <div>
                <label htmlFor="contact-immat" className="block text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-400 mb-2">Immatriculation / modèle</label>
                <input id="contact-immat" data-testid="contact-input-immat" value={form.immatriculation} onChange={set("immatriculation")} placeholder="AB-123-CD · Clio 5" className={INPUT_CLASS} />
              </div>
              <div>
                <label htmlFor="contact-service" className="block text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-400 mb-2">Service souhaité *</label>
                <select id="contact-service" data-testid="contact-input-service" value={form.service} onChange={set("service")} className={INPUT_CLASS}>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="contact-date" className="block text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-400 mb-2">Date souhaitée</label>
                <input id="contact-date" data-testid="contact-input-date" type="date" value={form.date_souhaitee} onChange={set("date_souhaitee")} className={INPUT_CLASS} />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="contact-message" className="block text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-400 mb-2">Message *</label>
                <textarea id="contact-message" data-testid="contact-input-message" required minLength={5} rows={4} value={form.message} onChange={set("message")} placeholder="Décrivez votre besoin (bruit, voyant, entretien...)" className={`${INPUT_CLASS} resize-none`} />
              </div>
            </div>

            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={loading}
              className="mt-8 group rounded-full bg-[#0066FF] hover:bg-[#0052CC] disabled:opacity-60 text-white font-semibold px-8 py-3.5 flex items-center gap-2 transition-[background-color,box-shadow,transform] duration-300 hover:shadow-[0_0_32px_rgba(0,102,255,0.55)] hover:-translate-y-0.5"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />}
              {loading ? "Envoi en cours..." : sent ? "Envoyer une autre demande" : "Envoyer ma demande"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
